import type { BrandDetails } from "schemas/generated/ai";
import type { UrlFormValues } from "schemas/url";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@replo/design-system/components/separator/Separator";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import Input from "@v1/components/common/designSystem/Input";
import ErrorMessage from "@v1/components/common/ErrorMessage/ErrorMessage";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { DeprecatedTextarea } from "@v1/components/designSystem/textarea/Textarea";
import { selectStoreShopifyUrl } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { aiClient } from "@v1/trpc/routes/ai";
import { analytics } from "@v2/features/infra/analytics/tracking";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useForm } from "react-hook-form";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { urlFormSchema } from "schemas/url";

import useBrandContext from "./useBrandContext";

type BrandContextMenuState =
  | {
      view: "detailsList";
      error?: boolean;
    }
  | {
      view: "pullFromUrl";
      isLoading: boolean;
    };

const BrandContextEditor: React.FC = () => {
  const logEvent = useLogAnalytics();

  const {
    brandContext,
    setBrandContext,
    isLoading: isLoadingBrandDetails,
  } = useBrandContext();

  const [draftBrandContext, setDraftBrandContext] =
    React.useState<BrandDetails | null>(null);

  // NOTE (Cole, 2025-03-10): UseEffect is bad, I'd prefer a redux slice like shop styles but don't think it's worth the effort until next time we touch this.
  React.useEffect(() => {
    if (!isLoadingBrandDetails && brandContext) {
      setDraftBrandContext(brandContext);
    }
  }, [isLoadingBrandDetails, brandContext]);

  const hasUnsavedChanges = React.useMemo(() => {
    if (!draftBrandContext || !brandContext) {
      return false;
    }

    return (
      draftBrandContext.brandName !== brandContext.brandName ||
      draftBrandContext.brandVoice !== brandContext.brandVoice ||
      draftBrandContext.whatBusinessSells !== brandContext.whatBusinessSells ||
      draftBrandContext.whoIsCustomer !== brandContext.whoIsCustomer
    );
  }, [draftBrandContext, brandContext]);

  const [brandContextMenuState, setBrandContextMenuState] =
    React.useState<BrandContextMenuState>({
      view: "detailsList",
    });

  const storeShopifyUrl = useEditorSelector(selectStoreShopifyUrl);
  const projectId = useCurrentProjectId();

  // Note (Evan, 2024-09-26): Hook form for the URL input (to get validation)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      url: storeShopifyUrl ?? "",
    },
    resolver: zodResolver(urlFormSchema),
  });

  const urlError = errors.url?.message;

  const onSubmitPullFromUrl = async ({ url }: UrlFormValues) => {
    setBrandContextMenuState({ view: "pullFromUrl", isLoading: true });

    analytics.logEvent("brandDetails.pullFromUrl", {
      url,
    });

    try {
      const result = await aiClient.generateBrandDetailsAndIndustry.query({
        url,
        projectId,
      });

      setBrandContext(result.brandDetails);
      setDraftBrandContext(result.brandDetails);

      setBrandContextMenuState({
        view: "detailsList",
      });
    } catch {
      setBrandContextMenuState({
        view: "detailsList",
        error: true,
      });
    }
  };

  const handleBrandVoiceChange = (value: string) => {
    if (!draftBrandContext) {
      return;
    }

    setDraftBrandContext({
      ...draftBrandContext,
      brandVoice: value,
    });
  };

  const handleWhatBusinessSellsChange = (value: string) => {
    if (!draftBrandContext) {
      return;
    }

    setDraftBrandContext({
      ...draftBrandContext,
      whatBusinessSells: value,
    });
  };

  const handleWhoIsCustomerChange = (value: string) => {
    if (!draftBrandContext) {
      return;
    }

    setDraftBrandContext({
      ...draftBrandContext,
      whoIsCustomer: value,
    });
  };

  const handleSave = () => {
    if (draftBrandContext && hasUnsavedChanges) {
      setBrandContext(draftBrandContext);
      successToast("Brand context saved");
    }
    logEvent("shopDetails.context.save");
  };

  const handleCancel = () => {
    if (brandContext) {
      setDraftBrandContext(brandContext);
    } else {
      setDraftBrandContext({
        brandName: "",
        brandVoice: "",
        whatBusinessSells: "",
        whoIsCustomer: "",
      });
    }
    logEvent("shopDetails.context.cancel");
  };

  return (
    <div className="text-default mt-0 flex flex-col gap-3 text-sm font-normal">
      <div className="flex flex-col gap-2">
        {exhaustiveSwitch(
          brandContextMenuState,
          "view",
        )({
          detailsList: ({ error }) => {
            if (isLoadingBrandDetails) {
              return (
                <div className="flex h-[244px] items-center justify-center">
                  <Spinner className="size-10" variant="primary" />
                </div>
              );
            }

            return (
              <>
                {error && (
                  <InlineAlert
                    variant="error"
                    onClose={() => {
                      // Note (Evan, 2024-09-27): Clear the error
                      setBrandContextMenuState({
                        view: "detailsList",
                      });
                    }}
                  >
                    Couldn't retrieve details from URL. Please try a different
                    URL.
                  </InlineAlert>
                )}
                <div className="flex w-full flex-row items-center justify-between">
                  <div className="text-xs font-semibold">Enter Context</div>
                  <DeprecatedButton
                    variant="link"
                    onClick={() => {
                      setBrandContextMenuState({
                        view: "pullFromUrl",
                        isLoading: false,
                      });
                    }}
                  >
                    Pull from URL
                  </DeprecatedButton>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-medium">Brand Name</div>
                  <Input
                    size="sm"
                    value={draftBrandContext?.brandName ?? ""}
                    layoutClassName="w-full h-9"
                    onChange={(e) => {
                      setDraftBrandContext({
                        ...draftBrandContext,
                        brandName: e.target.value,
                      });
                    }}
                    placeholder="Rockstar Bags"
                    maxLength={512}
                  />
                </div>
                <div className="text-xs font-medium">Tone and voice</div>
                <DeprecatedTextarea
                  size="sm"
                  textLength="short"
                  value={draftBrandContext?.brandVoice}
                  layoutClassName="h-[72px] w-full"
                  onChange={handleBrandVoiceChange}
                  placeholder="Fun, quirky, and playful - we use humor to connect with our audience and keep things light-hearted..."
                  maxLength={512}
                />
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-medium">
                    What are you selling?
                  </div>
                  <DeprecatedTextarea
                    size="sm"
                    textLength="short"
                    value={draftBrandContext?.whatBusinessSells}
                    layoutClassName="h-[72px] w-full"
                    onChange={handleWhatBusinessSellsChange}
                    placeholder="We sell handmade purses made from recycled concert tshirts"
                    maxLength={512}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-medium">Who do you sell to?</div>
                  <DeprecatedTextarea
                    size="sm"
                    textLength="short"
                    value={draftBrandContext?.whoIsCustomer}
                    layoutClassName="h-[72px] w-full"
                    onChange={handleWhoIsCustomerChange}
                    placeholder="Our customers are usually Gen Z teenagers who love TikTok"
                    maxLength={512}
                  />
                </div>

                <Separator className="my-2" />

                <div className="flex flex-row gap-2">
                  <DeprecatedButton
                    variant="primary"
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges}
                  >
                    Save
                  </DeprecatedButton>
                  <DeprecatedButton
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={!hasUnsavedChanges}
                  >
                    Cancel
                  </DeprecatedButton>
                </div>
              </>
            );
          },
          pullFromUrl: ({ isLoading }) => (
            <form
              className="flex flex-col gap-3"
              onSubmit={(data) => {
                void handleSubmit(onSubmitPullFromUrl)(data);
              }}
            >
              <div className="flex w-full flex-row items-center justify-between">
                <div className="text-xs font-semibold">Enter Context</div>
                <DeprecatedButton
                  variant="link"
                  onClick={() => {
                    return setBrandContextMenuState({
                      view: "detailsList",
                    });
                  }}
                >
                  Enter Manually
                </DeprecatedButton>
              </div>
              <div className="flex flex-col gap-2">
                <Input
                  autoComplete="off"
                  placeholder="https://www.mywebsite.com"
                  {...register("url")}
                  type="text"
                  size="base"
                  validityState={Boolean(urlError) ? "invalid" : "valid"}
                  autoFocus
                />
                {urlError && (
                  <ErrorMessage id="error-first-name" error={urlError} />
                )}
              </div>
              <div className="flex flex-row items-center justify-end">
                <DeprecatedButton
                  type="submit"
                  variant="primary"
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  Pull from URL
                </DeprecatedButton>
              </div>
            </form>
          ),
        })}
      </div>
    </div>
  );
};

export default BrandContextEditor;
